import { useState, useRef } from "react";
import { BookmarkSimple, XCircle } from "@phosphor-icons/react";
import CreateRecipeModal from "../../components/CreateRecipeModal";
import { TAG_COLORS } from "../../constants/tagColors";
import recipePlaceholderBg from "../../assets/recipe-placeholder-bg.svg";

// ── Shared bookmark icon (wired to favourites) ────────────────────────────────
function BookmarkBtn({ recipe, favouriteRecipes, toggleFavourite, appTheme }) {
  const isFav = (favouriteRecipes || []).includes(recipe.name);
  return (
    <button
      onClick={(e) => { e.stopPropagation(); toggleFavourite?.(recipe.name); }}
      style={{ width: "34px", height: "34px", borderRadius: "999px", border: "none", background: "rgba(0,0,0,0.35)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
    >
      <BookmarkSimple
        weight={isFav ? "fill" : "bold"}
        color="#ffffff"
        size={18}
      />
    </button>
  );
}


function tagPill(recipe, appTheme) {
  const firstTag = recipe.tags?.[0];
  if (!firstTag) return null;
  const label = firstTag.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  const bg = TAG_COLORS[firstTag] || appTheme.accentColor;
  return (
    <span style={{ background: bg, color: "#ffffff", fontSize: "9px", fontWeight: "700", padding: "3px 7px", borderRadius: "999px", letterSpacing: "0.06em", whiteSpace: "nowrap", display: "inline-block" }}>
      {label}
    </span>
  );
}

// ── Recipe card (uniform height) ─────────────────────────────────────────────
function RecipeCard({ recipe, isMobile, appTheme, openRecipeDetails, toggleFavourite, favouriteRecipes }) {
  return (
    <div
      onClick={() => openRecipeDetails?.(recipe, true)}
      style={{
        borderRadius: "18px",
        overflow: "hidden",
        cursor: "pointer",
        position: "relative",
        height: isMobile ? "225px" : "250px",
        background: recipe.image
          ? `url(${recipe.image}) center/cover no-repeat`
          : `url(${recipePlaceholderBg}) center/cover no-repeat`,
      }}
    >
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.72) 0%, transparent 60%)" }} />
      <div style={{ position: "absolute", top: "6px", right: "6px" }}>
        <BookmarkBtn recipe={recipe} favouriteRecipes={favouriteRecipes} toggleFavourite={toggleFavourite} appTheme={appTheme} />
      </div>
      <div style={{ position: "absolute", bottom: "10px", left: "12px", right: "12px" }}>
        <div style={{ color: "#ffffff", fontSize: isMobile ? "14px" : "15px", fontWeight: "500", fontFamily: appTheme.fontBody, lineHeight: 1.2, marginBottom: "7px", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {recipe.name}
        </div>
        <div style={{ display: "flex", gap: "5px", flexWrap: "wrap", alignItems: "center" }}>
          {recipe.time != null && (
            <span style={{ background: "rgba(0,0,0,0.52)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)", color: "#fff", fontSize: "10px", fontWeight: "700", padding: "3px 8px", borderRadius: "999px", letterSpacing: "0.06em" }}>
              {recipe.time} MIN
            </span>
          )}
          {tagPill(recipe, appTheme)}
        </div>
      </div>
    </div>
  );
}

// Keep aliases so MasonryGrid renderCol refs don't need changing
const CardTall  = RecipeCard;
const CardHoriz = RecipeCard;

// ── Masonry grid — 2-column stagger: left=TALL→HORIZ, right=HORIZ→TALL ────────
function MasonryGrid({ recipes, isMobile, appTheme, openRecipeDetails, toggleFavourite, favouriteRecipes }) {
  // Distribute into two columns
  const left = recipes.filter((_, i) => i % 2 === 0);
  const right = recipes.filter((_, i) => i % 2 === 1);
  const cardProps = { isMobile, appTheme, openRecipeDetails, toggleFavourite, favouriteRecipes };

  const renderCol = (items, startWithTall) =>
    items.map((recipe, idx) => {
      const isTall = startWithTall ? idx % 2 === 0 : idx % 2 === 1;
      return (
        <div key={recipe.name} style={{ marginBottom: "8px" }}>
          {isTall
            ? <CardTall recipe={recipe} {...cardProps} />
            : <CardHoriz recipe={recipe} {...cardProps} />
          }
        </div>
      );
    });

  return (
    <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
      <div style={{ flex: 1 }}>{renderCol(left, true)}</div>
      <div style={{ flex: 1 }}>{renderCol(right, false)}</div>
    </div>
  );
}

// ── Smart Drawer pill rows ────────────────────────────────────────────────────
const LOOKING_FOR_PILLS = [
  { label: "High Protein",   filter: "high-protein" },
  { label: "Quick",          filter: "quick" },
  { label: "Budget",         filter: "budget" },
  { label: "Under 500 kcal", filter: "under-500-kcal" },
];
const MEAL_TYPE_PILLS = [
  { label: "Breakfast", filter: "breakfast" },
  { label: "Lunch",     filter: "lunch" },
  { label: "Dinner",    filter: "dinner" },
];
const FILTER_LABELS = {
  "high-protein":   "High Protein",
  "quick":          "Quick",
  "budget":         "Budget",
  "under-500-kcal": "Under 500 kcal",
  "breakfast":      "Breakfast",
  "lunch":          "Lunch",
  "dinner":         "Dinner",
};
function getFilterColor(filter, appTheme) {
  return TAG_COLORS[filter] || appTheme.accentColor;
}

function RecipesTab({
  appTheme,
  setHasTriedToSaveAddRecipe,
  secondaryButtonStyle,
  recipeSearch,
  setRecipeSearch,
  sortBy,
  setSortBy,
  hoveredButton,
  setHoveredButton,
  activeFilters,
  applyFilters,
  clearAllFilters,
  recipeFilters,
  allRecipeFilters,
  toggleFilter,
  recipes,
  showMoreFilters,
  setShowMoreFilters,
  recipesTabFilteredRecipes,
  setAddRecipeWarning,
  setShowAddRecipeForm,
  showAddRecipeForm,
  modalOverlayStyle,
  modalContentStyle,
  clearAddRecipeForm,
  isMobile,
  hasTriedToSaveAddRecipe,
  isNewRecipeNameFilled,
  newRecipeName,
  setNewRecipeName,
  newRecipeImage,
  setNewRecipeImage,
  newRecipeCategory,
  newRecipeIngredients,
  newRecipeDescription,
  setNewRecipeDescription,
  newRecipeTime,
  setNewRecipeTime,
  newRecipeCalories,
  setNewRecipeCalories,
  newRecipeServings,
  setNewRecipeServings,
  setNewRecipeIngredients,
  isNewRecipeIngredientsFilled,
  isNewRecipeCaloriesFilled,
  isNewRecipeProteinFilled,
  isNewRecipeCarbsFilled,
  isNewRecipeFatsFilled,
  setNewRecipeCategory,
  isNewRecipeCategoryFilled,
  newRecipeTag,
  setNewRecipeTag,
  newRecipeProtein,
  setNewRecipeProtein,
  newRecipeCarbs,
  setNewRecipeCarbs,
  newRecipeFats,
  setNewRecipeFats,
  newRecipeInstructions,
  setNewRecipeInstructions,
  activeNewRecipeSections,
  setActiveNewRecipeSections,
  addRecipeWarning,
  isAddRecipeValid,
  addRecipe,
  recipeGridCard,
  closeButtonStyle,
  favouriteRecipes,
  recipesShowFavouritesOnly,
  setRecipesShowFavouritesOnly,
  recipesShowMyRecipesOnly,
  setRecipesShowMyRecipesOnly,
  openRecipeDetails,
  toggleFavourite,
}) {
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [activeMode, setActiveMode] = useState("browse"); // "browse" | "library"
  const [libraryFilter, setLibraryFilter] = useState("all"); // "all" | "saved" | "myrecipes"
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pendingFilters, setPendingFilters] = useState([]);
  const [showMaxMsg, setShowMaxMsg] = useState(false);
  const searchRef = useRef(null);

  const ACCENT = appTheme.accentColor;
  const MAX_FILTERS = 3;


  function togglePending(filter) {
    if (pendingFilters.includes(filter)) {
      setPendingFilters(prev => prev.filter(f => f !== filter));
      setShowMaxMsg(false);
    } else if (pendingFilters.length >= MAX_FILTERS) {
      setShowMaxMsg(true);
    } else {
      setPendingFilters(prev => [...prev, filter]);
      setShowMaxMsg(false);
    }
  }

  const pendingCount = (() => {
    const base = (recipes || []).filter(r => !r.custom)
      .filter(r => r.name.toLowerCase().includes(recipeSearch.toLowerCase()));
    if (pendingFilters.length === 0) return base.length;
    return base.filter(r =>
      pendingFilters.every(f => (r.tags || []).includes(f) || (r.categories || []).includes(f))
    ).length;
  })();

  function switchMode(mode) {
    setActiveMode(mode);
    setLibraryFilter("all");
    setDrawerOpen(false); // Browse filter drawer is mode-specific; don't bleed into Library
    setShowMaxMsg(false);
  }

  // Sort
  const sorted = [...recipesTabFilteredRecipes].sort((a, b) => {
    if (!sortField || !sortDirection) return 0;
    const dir = sortDirection === "asc" ? 1 : -1;
    if (sortField === "calories") return ((a.calories || 0) - (b.calories || 0)) * dir;
    if (sortField === "protein") return ((a.macros?.protein || 0) - (b.macros?.protein || 0)) * dir;
    if (sortField === "time") return ((a.time || 0) - (b.time || 0)) * dir;
    return 0;
  });

  // Mode-based filtering
  const browseRecipes = sorted.filter((r) => !r.custom);

  // Library ordering: saved recipes follow favouriteRecipes order (most recently saved first),
  // then any custom recipes not already in favourites
  const favList = favouriteRecipes || [];
  const savedOrdered = favList
    .map(name => sorted.find(r => r.name === name))
    .filter(Boolean);
  const customNotSaved = sorted.filter(r => r.custom && !favList.includes(r.name));
  const allLibraryRecipes = [...savedOrdered, ...customNotSaved];

  const libraryRecipes =
    libraryFilter === "saved"    ? savedOrdered :
    libraryFilter === "myrecipes"? sorted.filter((r) => r.custom) :
    allLibraryRecipes;



  return (
    <>
      {/* ── Title + Browse/Library toggle ───────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? "24px" : "30px", fontWeight: "600", fontFamily: appTheme.fontHeading, color: appTheme.primaryText, lineHeight: 1.1 }}>
          Recipes
        </h2>
        <div style={{
          display: "flex", borderRadius: "999px", overflow: "hidden",
          background: appTheme.softBackground || "rgba(0,0,0,0.06)",
          border: appTheme.cardBorder,
          padding: "3px", gap: "2px",
        }}>
          {["browse", "library"].map((mode) => {
            const isActive = activeMode === mode;
            return (
              <button
                key={mode}
                onClick={() => switchMode(mode)}
                style={{
                  padding: "6px 12px",
                  borderRadius: "999px",
                  border: "none",
                  fontSize: "12px",
                  fontWeight: "700",
                  background: isActive ? ACCENT : "transparent",
                  color: isActive ? "#ffffff" : appTheme.secondaryText,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  whiteSpace: "nowrap",
                }}
              >
                {mode === "browse" ? "Browse" : "My Library"}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Search bar ──────────────────────────────────────────────── */}
      <div style={{ position: "relative", marginBottom: drawerOpen ? "0" : "14px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* Input + inline chips container */}
          <div style={{
            flex: 1, display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px",
            padding: activeMode === "browse" && activeFilters.length > 0 ? "8px 12px" : "0",
            borderRadius: "999px", background: appTheme.inputBackground,
            border: `1.5px solid ${ACCENT}`, transition: "border 0.2s",
            position: "relative",
          }}>
            {/* Active chips — shown inside the bar when filters are set */}
            {activeMode === "browse" && activeFilters.map((filter) => {
              const color = getFilterColor(filter, appTheme);
              const label = FILTER_LABELS[filter] || filter;
              return (
                <span key={filter} style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: color, color: "#ffffff", padding: "3px 8px 3px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: "600", whiteSpace: "nowrap", flexShrink: 0 }}>
                  {label}
                  <button onClick={(e) => { e.stopPropagation(); const updated = activeFilters.filter(f => f !== filter); applyFilters(updated); setPendingFilters(updated); }} style={{ background: "rgba(255,255,255,0.3)", border: "none", color: "#ffffff", borderRadius: "999px", width: "14px", height: "14px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: "10px", fontWeight: "700", padding: 0, lineHeight: 1, flexShrink: 0 }} aria-label={`Remove ${label} filter`}>×</button>
                </span>
              );
            })}
            <input
              ref={searchRef}
              value={recipeSearch}
              onChange={(e) => setRecipeSearch(e.target.value)}
              onFocus={() => {
                if (activeMode === "browse") {
                  setPendingFilters([...activeFilters]);
                  setShowMaxMsg(false);
                  setDrawerOpen(true);
                }
              }}
              placeholder={activeMode === "browse" && activeFilters.length === 0 ? "Search all recipes..." : activeMode === "browse" ? "" : "Search your library..."}
              style={{
                flex: 1, minWidth: "60px", border: "none", background: "transparent",
                padding: activeMode === "browse" && activeFilters.length > 0 ? "3px 4px" : "13px 16px",
                paddingRight: recipeSearch.length > 0 ? "28px" : undefined,
                outline: "none", fontSize: "14px", color: appTheme.primaryText,
              }}
            />
            {recipeSearch.length > 0 && (
              <button
                onMouseDown={(e) => { e.preventDefault(); setRecipeSearch(""); searchRef.current?.focus(); }}
                style={{ position: "absolute", right: activeMode === "browse" && activeFilters.length > 0 ? "8px" : "14px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: "2px", display: "flex", alignItems: "center", justifyContent: "center", color: appTheme.mutedText, opacity: 0.6, lineHeight: 1 }}
                tabIndex={-1}
                aria-label="Clear search"
              >
                <XCircle size={16} weight="fill" />
              </button>
            )}
          </div>

          {drawerOpen ? (
            <button
              onClick={() => { setPendingFilters([...activeFilters]); setShowMaxMsg(false); setDrawerOpen(false); setRecipeSearch(""); searchRef.current?.blur(); }}
              style={{ background: "none", border: "none", cursor: "pointer", padding: "6px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
              aria-label="Close search"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke={ACCENT} strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          ) : null}
        </div>

        {/* ── Smart Drawer ──────────────────────────────────────────── */}
        {drawerOpen && (
          <div style={{
            marginTop: "10px", padding: "16px", borderRadius: "18px",
            background: appTheme.cardBackground, border: appTheme.cardBorder,
            boxShadow: "0 8px 32px rgba(15,23,42,0.12)",
            animation: "drawerSlideIn 220ms ease-out forwards",
            willChange: "transform",
          }}>
            {/* Row 1 — I'm looking for */}
            <div style={{ marginBottom: "14px" }}>
              <div style={{ fontSize: "11px", fontWeight: "700", color: appTheme.secondaryText, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: "8px" }}>
                I'm looking for…
              </div>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {LOOKING_FOR_PILLS.map(({ label, filter }) => {
                  const isPending = pendingFilters.includes(filter);
                  const atLimit = pendingFilters.length >= MAX_FILTERS;
                  const color = getFilterColor(filter, appTheme);
                  return (
                    <button
                      key={filter}
                      onClick={() => togglePending(filter)}
                      style={{
                        padding: "7px 14px", borderRadius: "999px", fontSize: "12px", fontWeight: "600",
                        border: isPending ? `1px solid ${color}` : appTheme.pillBorder,
                        background: isPending ? color : appTheme.pillBackground,
                        color: isPending ? "#ffffff" : appTheme.secondaryText,
                        cursor: "pointer", transition: "all 0.15s ease", whiteSpace: "nowrap",
                        opacity: !isPending && atLimit ? 0.4 : 1,
                      }}
                    >{label}</button>
                  );
                })}
              </div>
            </div>

            {/* Row 2 — Meal Type */}
            <div style={{ marginBottom: "14px" }}>
              <div style={{ fontSize: "11px", fontWeight: "700", color: appTheme.secondaryText, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: "8px" }}>
                Meal Type
              </div>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {MEAL_TYPE_PILLS.map(({ label, filter }) => {
                  const isPending = pendingFilters.includes(filter);
                  const atLimit = pendingFilters.length >= MAX_FILTERS;
                  const color = appTheme.accentColor;
                  return (
                    <button
                      key={filter}
                      onClick={() => togglePending(filter)}
                      style={{
                        padding: "7px 14px", borderRadius: "999px", fontSize: "12px", fontWeight: "600",
                        border: isPending ? `1px solid ${color}` : appTheme.pillBorder,
                        background: isPending ? color : appTheme.pillBackground,
                        color: isPending ? "#ffffff" : appTheme.secondaryText,
                        cursor: "pointer", transition: "all 0.15s ease", whiteSpace: "nowrap",
                        opacity: !isPending && atLimit ? 0.4 : 1,
                      }}
                    >{label}</button>
                  );
                })}
              </div>
            </div>

            {/* Max filter message */}
            {showMaxMsg && (
              <div style={{ fontSize: "12px", color: appTheme.mutedText, marginBottom: "8px" }}>
                Maximum {MAX_FILTERS} filters selected
              </div>
            )}

            {/* Apply + Clear All */}
            <div style={{ display: "flex", gap: "8px", marginBottom: "14px" }}>
              {(pendingFilters.length > 0 || activeFilters.length > 0) && (
                <button
                  onClick={() => { applyFilters([]); setPendingFilters([]); setShowMaxMsg(false); setDrawerOpen(false); searchRef.current?.blur(); }}
                  style={{ padding: "10px 16px", borderRadius: "999px", border: appTheme.pillBorder, background: appTheme.pillBackground, color: appTheme.secondaryText, fontSize: "13px", fontWeight: "600", cursor: "pointer", whiteSpace: "nowrap" }}
                >
                  Clear All
                </button>
              )}
              <button
                onClick={() => { applyFilters(pendingFilters); setDrawerOpen(false); searchRef.current?.blur(); }}
                style={{ flex: 1, padding: "10px 16px", borderRadius: "999px", border: "none", background: ACCENT, color: "#ffffff", fontSize: "13px", fontWeight: "700", cursor: "pointer", whiteSpace: "nowrap" }}
              >
                {pendingFilters.length === 0 ? "Show all recipes" : `Show ${pendingCount} recipes`}
              </button>
            </div>

            {/* Row 3 — Quick search */}
            <div>
              <div style={{ fontSize: "11px", fontWeight: "700", color: appTheme.secondaryText, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: "8px" }}>
                Quick search
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                {["Spaghetti Bolognese", "Chicken Stir Fry", "Salmon Bowl"].map((name) => (
                  <button
                    key={name}
                    onClick={() => { setRecipeSearch(name); setDrawerOpen(false); }}
                    style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 0", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}
                  >
                    <svg width="16" height="11" viewBox="0 0 144 93" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                      <path d="M143.503 5.125V46.125C143.503 47.4842 142.963 48.7878 142.002 49.7489C141.041 50.71 139.737 51.25 138.378 51.25C137.019 51.25 135.715 50.71 134.754 49.7489C133.793 48.7878 133.253 47.4842 133.253 46.125V17.4955L80.5038 70.2509C80.0278 70.7274 79.4626 71.1055 78.8404 71.3634C78.2183 71.6213 77.5514 71.754 76.8778 71.754C76.2043 71.754 75.5374 71.6213 74.9153 71.3634C74.2931 71.1055 73.7279 70.7274 73.2519 70.2509L51.2528 48.2455L8.75379 90.7509C7.79213 91.7126 6.48784 92.2528 5.12785 92.2528C3.76786 92.2528 2.46357 91.7126 1.50191 90.7509C0.540254 89.7893 0 88.485 0 87.125C0 85.765 0.540254 84.4607 1.50191 83.4991L47.6269 37.3741C48.1029 36.8976 48.6681 36.5195 49.2903 36.2616C49.9124 36.0037 50.5793 35.871 51.2528 35.871C51.9264 35.871 52.5933 36.0037 53.2154 36.2616C53.8376 36.5195 54.4028 36.8976 54.8788 37.3741L76.8778 59.3795L126.007 10.25H97.3779C96.0186 10.25 94.7151 9.71004 93.7539 8.74892C92.7928 7.7878 92.2529 6.48423 92.2529 5.125C92.2529 3.76577 92.7928 2.4622 93.7539 1.50108C94.7151 0.539955 96.0186 0 97.3779 0H138.378C139.737 0 141.041 0.539955 142.002 1.50108C142.963 2.4622 143.503 3.76577 143.503 5.125Z" fill={appTheme.secondaryText}/>
                    </svg>
                    <span style={{ fontSize: "14px", color: appTheme.primaryText }}>{name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Backdrop blur when drawer open */}
      {drawerOpen && (
        <div
          onClick={() => { setPendingFilters([...activeFilters]); setShowMaxMsg(false); setDrawerOpen(false); searchRef.current?.blur(); }}
          style={{ position: "fixed", inset: 0, zIndex: -1 }}
        />
      )}

      {/* ── Library filter pill — fixed above nav bar ───────────────── */}
      {activeMode === "library" && !drawerOpen && (
        <div style={{
          position: "fixed",
          bottom: isMobile ? "100px" : "40px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 200,
          display: "flex",
          borderRadius: "999px",
          overflow: "hidden",
          background: "rgba(255,255,255,0.22)",
          backdropFilter: "blur(20px) saturate(180%)",
          WebkitBackdropFilter: "blur(20px) saturate(180%)",
          boxShadow: "0 4px 24px rgba(0,0,0,0.14), inset 0 0 0 1px rgba(255,255,255,0.35)",
          padding: "3px",
          gap: "2px",
        }}>
          {[
            { key: "all",       label: "All" },
            { key: "saved",     label: "Saved" },
            { key: "myrecipes", label: "My Recipes" },
          ].map(({ key, label }) => {
            const isActive = libraryFilter === key;
            return (
              <button
                key={key}
                onClick={() => setLibraryFilter(key)}
                style={{
                  padding: "9px 18px", borderRadius: "999px", border: "none",
                  textAlign: "center",
                  background: isActive ? ACCENT : "transparent",
                  color: isActive ? "#ffffff" : appTheme.primaryText,
                  fontWeight: isActive ? "700" : "600",
                  fontSize: "13px", cursor: "pointer",
                  transition: "all 0.18s ease", whiteSpace: "nowrap",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Count row ────────────────────────────────────────────────── */}
      {!drawerOpen && (
        <div style={{ marginBottom: "16px", fontSize: "13px", color: appTheme.secondaryText }}>
          {activeMode === "browse"
            ? `${browseRecipes.length} ${browseRecipes.length === 1 ? "recipe" : "recipes"}`
            : `${libraryRecipes.length} ${libraryRecipes.length === 1 ? "recipe" : "recipes"}`}
        </div>
      )}

      {/* ── Recipe grids — both always mounted, CSS show/hide preserves image state ── */}
      {!drawerOpen && <>
          {/* BROWSE — always in DOM */}
          <div style={{ display: activeMode === "browse" ? "block" : "none", animation: activeMode === "browse" ? "fadeInGrid 150ms ease" : "none" }}>
            {browseRecipes.length === 0 && (
              <div style={{ padding: "20px", borderRadius: "16px", background: appTheme.softBackground, border: appTheme.cardBorder, color: appTheme.secondaryText }}>
                No recipes match your search or filters.
              </div>
            )}
            {browseRecipes.length > 0 && (
              <MasonryGrid
                recipes={browseRecipes}
                isMobile={isMobile}
                appTheme={appTheme}
                openRecipeDetails={openRecipeDetails}
                toggleFavourite={toggleFavourite}
                favouriteRecipes={favouriteRecipes}
              />
            )}
          </div>

          {/* LIBRARY — always in DOM */}
          <div style={{ display: activeMode === "library" ? "block" : "none", animation: activeMode === "library" ? "fadeInGrid 150ms ease" : "none" }}>
            {libraryFilter === "saved" && libraryRecipes.length === 0 && (
              <div style={{ textAlign: "center", padding: "48px 24px" }}>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: "12px" }}>
                  <BookmarkSimple weight="regular" color={appTheme.mutedText} size={40} />
                </div>
                <div style={{ fontSize: "17px", fontWeight: "500", color: appTheme.primaryText, marginBottom: "8px", fontFamily: appTheme.fontHeading }}>No saved recipes yet</div>
                <div style={{ fontSize: "14px", color: appTheme.secondaryText, lineHeight: 1.6, maxWidth: "240px", margin: "0 auto 20px" }}>
                  Tap the bookmark on any recipe to save it here for quick access.
                </div>
                <button onClick={() => switchMode("browse")} style={{ padding: "10px 22px", borderRadius: "999px", border: "none", background: ACCENT, color: "#fff", fontSize: "14px", fontWeight: "700", cursor: "pointer" }}>
                  Browse Recipes
                </button>
              </div>
            )}
            {!(libraryFilter === "saved" && libraryRecipes.length === 0) && (() => {
              const gap = isMobile ? "6px" : "8px";
              const showCreate = libraryFilter !== "saved";
              // Left: Create Recipe + odd-indexed recipes (1, 3, 5...)
              // Right: even-indexed recipes (0, 2, 4...) — newest (index 0) sits top-right
              const leftRecipes  = libraryRecipes.filter((_, i) => showCreate ? i % 2 === 1 : i % 2 === 0);
              const rightRecipes = libraryRecipes.filter((_, i) => showCreate ? i % 2 === 0 : i % 2 === 1);
              return (
                <div style={{ display: "flex", gap, alignItems: "flex-start", marginBottom: "16px" }}>
                  {/* Left column */}
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap }}>
                    {showCreate && (
                      <div
                        onClick={() => { setAddRecipeWarning(""); setShowAddRecipeForm(true); }}
                        style={{ borderRadius: isMobile ? "22px" : "28px", border: `2px dashed ${appTheme.accentColor}55`, background: appTheme.accentLight, cursor: "pointer", minHeight: isMobile ? "180px" : "240px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px" }}
                      >
                        <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: appTheme.accentColor, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <span style={{ color: "#fff", fontSize: "28px", fontWeight: "300", lineHeight: 1 }}>+</span>
                        </div>
                        <div style={{ fontSize: "13px", fontWeight: "700", color: ACCENT, textAlign: "center" }}>Create Recipe</div>
                      </div>
                    )}
                    {leftRecipes.map((recipe) => (
                      <div key={recipe.id || recipe.name}>{recipeGridCard(recipe, { hideCustomBadge: libraryFilter === "myrecipes" })}</div>
                    ))}
                  </div>
                  {/* Right column — newest recipe (index 0) sits at top */}
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap }}>
                    {rightRecipes.map((recipe) => (
                      <div key={recipe.id || recipe.name}>{recipeGridCard(recipe, { hideCustomBadge: libraryFilter === "myrecipes" })}</div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
      </>}

      {/* ── Create Recipe Modal ─────────────────────────────────────── */}
      <CreateRecipeModal
        isOpen={showAddRecipeForm}
        appTheme={appTheme}
        isMobile={isMobile}
        newRecipeName={newRecipeName} setNewRecipeName={setNewRecipeName}
        newRecipeImage={newRecipeImage} setNewRecipeImage={setNewRecipeImage}
        newRecipeCategory={newRecipeCategory} setNewRecipeCategory={setNewRecipeCategory}
        newRecipeIngredients={newRecipeIngredients} setNewRecipeIngredients={setNewRecipeIngredients}
        newRecipeDescription={newRecipeDescription} setNewRecipeDescription={setNewRecipeDescription}
        newRecipeTime={newRecipeTime} setNewRecipeTime={setNewRecipeTime}
        newRecipeCalories={newRecipeCalories} setNewRecipeCalories={setNewRecipeCalories}
        newRecipeServings={newRecipeServings} setNewRecipeServings={setNewRecipeServings}
        newRecipeProtein={newRecipeProtein} setNewRecipeProtein={setNewRecipeProtein}
        newRecipeCarbs={newRecipeCarbs} setNewRecipeCarbs={setNewRecipeCarbs}
        newRecipeFats={newRecipeFats} setNewRecipeFats={setNewRecipeFats}
        newRecipeTag={newRecipeTag} setNewRecipeTag={setNewRecipeTag}
        newRecipeInstructions={newRecipeInstructions} setNewRecipeInstructions={setNewRecipeInstructions}
        activeNewRecipeSections={activeNewRecipeSections} setActiveNewRecipeSections={setActiveNewRecipeSections}
        hasTriedToSaveAddRecipe={hasTriedToSaveAddRecipe} setHasTriedToSaveAddRecipe={setHasTriedToSaveAddRecipe}
        isNewRecipeNameFilled={isNewRecipeNameFilled} isNewRecipeCategoryFilled={isNewRecipeCategoryFilled}
        isNewRecipeIngredientsFilled={isNewRecipeIngredientsFilled} isNewRecipeCaloriesFilled={isNewRecipeCaloriesFilled}
        isNewRecipeProteinFilled={isNewRecipeProteinFilled} isNewRecipeCarbsFilled={isNewRecipeCarbsFilled}
        isNewRecipeFatsFilled={isNewRecipeFatsFilled} isAddRecipeValid={isAddRecipeValid}
        addRecipe={addRecipe} clearAddRecipeForm={clearAddRecipeForm}
        addRecipeWarning={addRecipeWarning} setAddRecipeWarning={setAddRecipeWarning}
        setShowAddRecipeForm={setShowAddRecipeForm}
      />

      {/* ── Filters modal (unchanged) ──────────────────────────────── */}
      {showMoreFilters && (
        <div onClick={() => setShowMoreFilters(false)} style={{ ...modalOverlayStyle, alignItems: "flex-start", justifyContent: "center", paddingTop: isMobile ? "18px" : "28px" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: isMobile ? "94vw" : "760px", marginTop: isMobile ? "28px" : "36px", alignSelf: "flex-start", borderRadius: "28px", padding: isMobile ? "22px 20px 24px" : "28px 28px 30px", background: appTheme.modalBackground, border: appTheme.cardBorder, boxShadow: "0 24px 60px rgba(15,23,42,0.2)", backdropFilter: "blur(18px) saturate(180%)", WebkitBackdropFilter: "blur(18px) saturate(180%)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
              <div style={{ fontSize: isMobile ? "18px" : "20px", fontWeight: "500", color: appTheme.primaryText, fontFamily: appTheme.fontHeading }}>Filters</div>
              <button onClick={() => setShowMoreFilters(false)} style={closeButtonStyle(hoveredButton, "close-more-filters", appTheme, "close-more-filters")}>×</button>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "16px" }}>
              <button onClick={clearAllFilters} style={{ padding: "9px 14px", borderRadius: "999px", border: activeFilters.length === 0 ? appTheme.accentBorder : appTheme.pillBorder, background: activeFilters.length === 0 ? appTheme.accentColor : appTheme.pillBackground, color: activeFilters.length === 0 ? "#ffffff" : ACCENT, fontWeight: "600", fontSize: "13px", cursor: "pointer" }}>All</button>
              {allRecipeFilters.map((filter) => {
                const isActive = activeFilters.includes(filter.value);
                return (
                  <button key={filter.value} onClick={() => toggleFilter(filter.value)} style={{ padding: "9px 14px", borderRadius: "999px", border: isActive ? appTheme.accentBorder : appTheme.pillBorder, background: isActive ? appTheme.accentColor : appTheme.pillBackground, color: isActive ? "#ffffff" : ACCENT, fontWeight: "600", fontSize: "13px", cursor: "pointer", transition: "all 0.15s ease" }}>{filter.label}</button>
                );
              })}
            </div>

            <div style={{ height: "1px", background: appTheme.cardBorder.replace("1px solid ", ""), marginBottom: "14px" }} />

            <div style={{ marginBottom: "20px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
                <div style={{ fontSize: "11px", fontWeight: "700", color: appTheme.secondaryText, letterSpacing: "0.06em", textTransform: "uppercase" }}>Sort By</div>
                <div style={{ display: "flex", gap: "6px" }}>
                  {[{ label: "↑", value: "asc" }, { label: "↓", value: "desc" }].map((dir) => {
                    const isActive = sortDirection === dir.value;
                    return <button key={dir.value} onClick={() => setSortDirection(isActive ? null : dir.value)} style={{ width: "30px", height: "30px", borderRadius: "999px", border: isActive ? appTheme.accentBorder : appTheme.pillBorder, background: isActive ? appTheme.accentColor : appTheme.pillBackground, color: isActive ? "#ffffff" : appTheme.primaryText, fontSize: "14px", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>{dir.label}</button>;
                  })}
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {[{ label: "Calories", value: "calories" }, { label: "Protein", value: "protein" }, { label: "Time", value: "time" }].map((option) => {
                  const isActive = sortField === option.value;
                  const isReady = isActive && sortDirection;
                  return <button key={option.value} onClick={() => setSortField(isActive ? null : option.value)} style={{ padding: "8px 14px", borderRadius: "999px", border: isReady ? appTheme.accentBorder : appTheme.pillBorder, background: isReady ? appTheme.accentColor : appTheme.pillBackground, color: isReady ? "#ffffff" : appTheme.primaryText, fontWeight: "600", fontSize: "13px", cursor: "pointer", opacity: isActive && !sortDirection ? 0.6 : 1 }}>{option.label}</button>;
                })}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: "13px", color: appTheme.secondaryText }}>
                {activeFilters.length === 0 && !sortField ? "No filters active" : `${activeFilters.length > 0 ? `${activeFilters.length} filter${activeFilters.length === 1 ? "" : "s"}` : ""}${sortField && sortDirection ? `${activeFilters.length > 0 ? " • " : ""}sorted by ${sortField}` : ""}`}
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <button onClick={() => { clearAllFilters(); setSortField(null); setSortDirection(null); }} style={secondaryButtonStyle(appTheme, hoveredButton, "clear-filters")}>Clear</button>
                <button onClick={() => setShowMoreFilters(false)} style={{ ...secondaryButtonStyle(appTheme, hoveredButton, "close-filters"), background: appTheme.accentLight, border: appTheme.accentBorder, color: ACCENT }}>Done</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ height: isMobile ? "100px" : "40px" }} />
    </>
  );
}

export default RecipesTab;
